import { useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Progress,
  WarningPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';

type Summary = {
  status: string;
  teams: number;
  members: number;
  repoPermissions: number;
  lastSyncedAt?: string;
};

type UserRow = {
  user_login: string;
  team_count: number;
  repo_permission_count: number;
};

type UserRepo = {
  repo: string;
  visibility: 'public' | 'private';
  permission: 'read' | 'write' | 'admin';
  source: 'team' | 'direct';
  source_team_slug?: string;
};

type TeamRow = {
  org: string;
  team_slug: string;
  team_id: number;
  team_name: string;
  description?: string | null;
  synced_at?: string;
};

type TeamMember = {
  user_login: string;
  user_id?: number;
  member_role?: string;
};

type TeamRepo = {
  repo: string;
  permission: 'read' | 'write' | 'admin';
};

type TeamDetails = {
  team: TeamRow | null;
  members: TeamMember[];
  repos: TeamRepo[];
};

export const AdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [summary, setSummary] = useState<Summary | undefined>();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [userRepos, setUserRepos] = useState<UserRepo[]>([]);
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
  const [reposLoading, setReposLoading] = useState(false);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);
  const [reposError, setReposError] = useState<string | undefined>();
  const [teamDetailsError, setTeamDetailsError] = useState<
    string | undefined
  >();
  const [reposVisibility, setReposVisibility] = useState<
    'all' | 'public' | 'private'
  >('all');
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const loadData = async () => {
    try {
      setError(undefined);
      setLoading(true);

      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const [summaryRes, usersRes, teamsRes] = await Promise.all([
        fetchApi.fetch(`${adminBaseUrl}/github/summary`),
        fetchApi.fetch(`${adminBaseUrl}/github/users`),
        fetchApi.fetch(`${adminBaseUrl}/github/teams`),
      ]);

      if (!summaryRes.ok) {
        throw new Error(`Summary failed: ${summaryRes.statusText}`);
      }

      if (!usersRes.ok) {
        throw new Error(`Users failed: ${usersRes.statusText}`);
      }

      if (!teamsRes.ok) {
        throw new Error(`Teams failed: ${teamsRes.statusText}`);
      }

      const summaryJson = await summaryRes.json();
      const usersJson = await usersRes.json();
      const teamsJson = await teamsRes.json();

      setSummary(summaryJson);
      setUsers(usersJson?.users ?? []);
      setTeams(teamsJson?.teams ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    try {
      setSyncing(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(`${adminBaseUrl}/sync`, {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error(`Sync failed: ${res.statusText}`);
      }
      await loadData();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSyncing(false);
    }
  };

  const loadUserRepos = async (
    username: string,
    visibility: 'all' | 'public' | 'private' = 'all',
  ) => {
    try {
      setReposError(undefined);
      setReposLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/users/${username}/repos?visibility=${visibility}`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load repos: ${res.statusText}`);
      }
      const data = await res.json();
      setUserRepos(data.repos || []);
    } catch (e: unknown) {
      setReposError(e instanceof Error ? e.message : String(e));
    } finally {
      setReposLoading(false);
    }
  };

  const handleUserClick = async (user: UserRow) => {
    setSelectedUser(user.user_login);
    await loadUserRepos(user.user_login, reposVisibility);
  };

  const loadTeamDetails = async (teamSlug: string) => {
    try {
      setTeamDetailsError(undefined);
      setTeamDetailsLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/teams/${teamSlug}`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load team details: ${res.statusText}`);
      }
      const data = await res.json();
      setTeamDetails(data);
    } catch (e: unknown) {
      setTeamDetailsError(e instanceof Error ? e.message : String(e));
    } finally {
      setTeamDetailsLoading(false);
    }
  };

  const handleTeamClick = async (team: TeamRow) => {
    setSelectedTeam(team.team_slug);
    await loadTeamDetails(team.team_slug);
  };

  const handleCloseUserDialog = () => {
    setSelectedUser(null);
    setUserRepos([]);
    setReposError(undefined);
  };

  const handleCloseTeamDialog = () => {
    setSelectedTeam(null);
    setTeamDetails(null);
    setTeamDetailsError(undefined);
  };

  const handleVisibilityChange = async (
    newVisibility: 'all' | 'public' | 'private',
  ) => {
    setReposVisibility(newVisibility);
    if (selectedUser) {
      await loadUserRepos(selectedUser, newVisibility);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userColumns: TableColumn<UserRow>[] = [
    { title: 'User', field: 'user_login' },
    { title: 'Teams', field: 'team_count', type: 'numeric' },
    { title: 'Repo perms', field: 'repo_permission_count', type: 'numeric' },
  ];

  const repoColumns: TableColumn<UserRepo>[] = [
    { title: 'Repository', field: 'repo' },
    { title: 'Visibility', field: 'visibility' },
    { title: 'Permission', field: 'permission' },
    { title: 'Source', field: 'source' },
    { title: 'Team', field: 'source_team_slug' },
  ];

  const teamColumns: TableColumn<TeamRow>[] = [
    { title: 'Team', field: 'team_name' },
    { title: 'Slug', field: 'team_slug' },
    { title: 'Description', field: 'description' },
  ];

  const teamMemberColumns: TableColumn<TeamMember>[] = [
    { title: 'User', field: 'user_login' },
    { title: 'Role', field: 'member_role' },
  ];

  const teamRepoColumns: TableColumn<TeamRepo>[] = [
    { title: 'Repository', field: 'repo' },
    { title: 'Permission', field: 'permission' },
  ];

  let body;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <WarningPanel title="Error" message={error} />;
  } else {
    body = (
      <>
        <InfoCard
          title="GitHub Sync"
          action={
            <Button variant="contained" color="primary" onClick={triggerSync}>
              {syncing ? 'Syncing...' : 'Run Sync'}
            </Button>
          }
        >
          <p>Teams: {summary?.teams ?? 0}</p>
          <p>Members: {summary?.members ?? 0}</p>
          <p>Repo permissions: {summary?.repoPermissions ?? 0}</p>
          <p>
            Last synced:{' '}
            {summary?.lastSyncedAt
              ? new Date(summary.lastSyncedAt).toLocaleString()
              : 'n/a'}
          </p>
        </InfoCard>

        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}
        >
          <InfoCard title="Users">
            <Table
              title="Users"
              options={{ paging: true, pageSize: 10 }}
              columns={userColumns}
              data={users}
              onRowClick={(_, rowData) => handleUserClick(rowData as UserRow)}
            />
          </InfoCard>
          <InfoCard title="Teams">
            <Table
              title="Teams"
              options={{ paging: true, pageSize: 10 }}
              columns={teamColumns}
              data={teams}
              onRowClick={(_, rowData) => handleTeamClick(rowData as TeamRow)}
            />
          </InfoCard>
        </div>
      </>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Admin Dashboard" subtitle="Access governance and sync" />
      <Content>{body}</Content>
      <Dialog
        open={selectedUser !== null}
        onClose={handleCloseUserDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Repositories for {selectedUser}</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 16 }}>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 150 }}
            >
              <InputLabel>Visibility</InputLabel>
              <Select
                value={reposVisibility}
                onChange={e =>
                  handleVisibilityChange(
                    e.target.value as 'all' | 'public' | 'private',
                  )
                }
                label="Visibility"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
          </div>
          {reposLoading && <Progress />}
          {!reposLoading && reposError && (
            <WarningPanel title="Error" message={reposError} />
          )}
          {!reposLoading && !reposError && (
            <Table
              title="Repositories"
              options={{ paging: true, pageSize: 10 }}
              columns={repoColumns}
              data={userRepos}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selectedTeam !== null}
        onClose={handleCloseTeamDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {teamDetails?.team
            ? `${teamDetails.team.team_name} Details`
            : 'Team Details'}
        </DialogTitle>
        <DialogContent>
          {teamDetailsLoading && <Progress />}
          {!teamDetailsLoading && teamDetailsError && (
            <WarningPanel title="Error" message={teamDetailsError} />
          )}
          {!teamDetailsLoading && !teamDetailsError && teamDetails && (
            <>
              {teamDetails.team && (
                <div style={{ marginBottom: 16 }}>
                  <InfoCard title="Team Information">
                    <p>
                      <strong>Name:</strong> {teamDetails.team.team_name}
                    </p>
                    <p>
                      <strong>Slug:</strong> {teamDetails.team.team_slug}
                    </p>
                    {teamDetails.team.description && (
                      <p>
                        <strong>Description:</strong>{' '}
                        {teamDetails.team.description}
                      </p>
                    )}
                    <p>
                      <strong>Members:</strong> {teamDetails.members.length}
                    </p>
                    <p>
                      <strong>Repositories:</strong> {teamDetails.repos.length}
                    </p>
                  </InfoCard>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <InfoCard title="Members">
                  <Table
                    title="Members"
                    options={{ paging: true, pageSize: 10 }}
                    columns={teamMemberColumns}
                    data={teamDetails.members}
                  />
                </InfoCard>
              </div>
              <div style={{ marginTop: 16 }}>
                <InfoCard title="Repositories">
                  <Table
                    title="Repositories"
                    options={{ paging: true, pageSize: 10 }}
                    columns={teamRepoColumns}
                    data={teamDetails.repos}
                  />
                </InfoCard>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeamDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default AdminPage;
